import { ApiConfigration } from '@/hotdesk-ui/common-classes/api-configuration';
import { BookingStatusTabs } from '@/hotdesk-ui/enum/booking-status';
import { BookingService } from '@/hotdesk-ui/services/booking.service';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Booking, Currency, InlineResponse2001 } from 'hotdesk_typescript';
import { RootState } from '../store';
import { serializeBookingDates } from '@/hotdesk-ui/utils/bookingDate';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { buildBookingListParams } from '@/hotdesk-ui/helpers/booking-filter-params';
import { commonConstant } from '@/hotdesk-ui/constants/common.constant';
import {
    applyBookingListPriceFilter,
    hasBookingPriceFilter
} from '@/hotdesk-ui/helpers/booking-filter-price';

export interface InitialStateListing {
    userPendingBookings: Booking[];
    userUpcomingBookings: Booking[];
    userOngoingBookings: Booking[];
    userHistoryBookings: Booking[];
    activeButton: BookingStatusTabs;
    defaultLanguage: string;
    pendingBtnLoading?: boolean;
    upcomingBtnLoading?: boolean;
    ongoingBtnLoading?: boolean;
    historyBtnLoading?: boolean;
    error?: string | null;
    pendingPage: number;
    upcomingPage: number;
    ongoingPage: number;
    historyPage: number;
    radius: number;
    filters: { [key: string]: string };
    isPendingLoading: boolean;
    isUpcomingLoading: boolean;
    isOngoingLoading: boolean;
    isHistoryLoading: boolean;
    totalPending: number;
    totalUpcoming: number;
    totalOngoing: number;
    totalHistory: number;
}

export const initialState: InitialStateListing = {
    userPendingBookings: [],
    userUpcomingBookings: [],
    userOngoingBookings: [],
    userHistoryBookings: [],
    activeButton: BookingStatusTabs.pending,
    defaultLanguage: 'en',
    pendingBtnLoading: false,
    upcomingBtnLoading: false,
    ongoingBtnLoading: false,
    historyBtnLoading: false,
    error: null,
    pendingPage: 1,
    upcomingPage: 1,
    ongoingPage: 1,
    historyPage: 1,
    radius: 15,
    filters: {},
    isPendingLoading: false,
    isUpcomingLoading: false,
    isOngoingLoading: false,
    isHistoryLoading: false,
    totalPending: 0,
    totalUpcoming: 0,
    totalOngoing: 0,
    totalHistory: 0
};

const PRICE_FILTER_FETCH_LIMIT = 500;

const fetchFilteredBookingList = async (
    tab: BookingStatusTabs,
    filters: Record<string, string>,
    pagination: { limit: number; offset: number },
    defaultLanguage: string,
    currencyList: Currency[]
): Promise<InlineResponse2001> => {
    const apiConfig = await new ApiConfigration().getApiConfig(defaultLanguage);
    const service = new BookingService(apiConfig);

    if (hasBookingPriceFilter(filters)) {
        const batchRes = await service.bookingList(
            buildBookingListParams(tab, filters, {
                limit: PRICE_FILTER_FETCH_LIMIT,
                offset: 0
            })
        );
        const filtered = applyBookingListPriceFilter(
            batchRes,
            filters,
            currencyList
        );
        const results = filtered.results ?? [];
        return {
            ...filtered,
            results: results.slice(
                pagination.offset,
                pagination.offset + pagination.limit
            )
        };
    }

    const response = await service.bookingList(
        buildBookingListParams(tab, filters, pagination)
    );
    return applyBookingListPriceFilter(response, filters, currencyList);
};

// Get Pending Bookings
export const getPendingData = createAsyncThunk(
    'booking/getPendingData',
    async (
        {
            page,
            limit = commonConstant.limit,
            filters: filtersOverride
        }: {
            page: number;
            limit?: number;
            filters?: Record<string, string>;
        },
        { getState }
    ) => {
        const state = getState() as RootState;
        const { defaultLanguage, filters: storeFilters } = state.booking;
        const filters = filtersOverride ?? storeFilters;
        const currencyList = state.currency.currency;
        try {
            const pendingRes = await fetchFilteredBookingList(
                BookingStatusTabs.pending,
                filters,
                {
                    limit,
                    offset: (page - 1) * limit
                },
                defaultLanguage,
                currencyList
            );
            return serializeBookingDates(pendingRes);
        } catch (error) {
            console.error('Error getting results', error);
            throw error;
        }
    }
);
// Get Upcoming Bookings
export const getUpcomingData = createAsyncThunk(
    'booking/getUpcomingData',
    async (
        {
            page,
            limit = commonConstant.limit,
            filters: filtersOverride
        }: {
            page: number;
            limit?: number;
            filters?: Record<string, string>;
        },
        { getState }
    ) => {
        const state = getState() as RootState;
        const { defaultLanguage, filters: storeFilters } = state.booking;
        const filters = filtersOverride ?? storeFilters;
        const currencyList = state.currency.currency;
        try {
            const upcomingRes = await fetchFilteredBookingList(
                BookingStatusTabs.upcoming,
                filters,
                {
                    limit,
                    offset: (page - 1) * limit
                },
                defaultLanguage,
                currencyList
            );
            return serializeBookingDates(upcomingRes);
        } catch (error) {
            console.error('Error getting results', error);
            throw error;
        }
    }
);
// Get Ongoing Bookings
export const getOngoingData = createAsyncThunk(
    'booking/getOngoingData',
    async (
        {
            page,
            limit = commonConstant.limit,
            filters: filtersOverride
        }: {
            page: number;
            limit?: number;
            filters?: Record<string, string>;
        },
        { getState }
    ) => {
        const state = getState() as RootState;
        const { defaultLanguage, filters: storeFilters } = state.booking;
        const filters = filtersOverride ?? storeFilters;
        const currencyList = state.currency.currency;
        try {
            const ongoingRes = await fetchFilteredBookingList(
                BookingStatusTabs.ongoing,
                filters,
                {
                    limit,
                    offset: (page - 1) * limit
                },
                defaultLanguage,
                currencyList
            );
            return serializeBookingDates(ongoingRes);
        } catch (error) {
            console.error('Error getting results', error);
            throw error;
        }
    }
);
// Get History Bookings
export const getHistoryData = createAsyncThunk(
    'booking/getHistoryData',
    async (
        {
            page,
            limit = commonConstant.limit,
            filters: filtersOverride
        }: {
            page: number;
            limit?: number;
            filters?: Record<string, string>;
        },
        { getState }
    ) => {
        const state = getState() as RootState;
        const { defaultLanguage, filters: storeFilters } = state.booking;
        const filters = filtersOverride ?? storeFilters;
        const currencyList = state.currency.currency;
        try {
            const historyRes = await fetchFilteredBookingList(
                BookingStatusTabs.history,
                filters,
                {
                    limit,
                    offset: (page - 1) * limit
                },
                defaultLanguage,
                currencyList
            );
            return serializeBookingDates(historyRes);
        } catch (error) {
            console.error('Error getting results', error);
            throw error;
        }
    }
);

export const bookingSlice = createSlice({
    name: 'booking',
    initialState,
    reducers: {
        setActiveButton(state, action: PayloadAction<BookingStatusTabs>) {
            state.activeButton = action.payload;
        },
       
        setPendingPage(state, action: PayloadAction<number>) {
            state.pendingPage = action.payload;
        },
        setUpcomingPage(state, action: PayloadAction<number>) {
            state.upcomingPage = action.payload;
        },
        setOngoingPage(state, action: PayloadAction<number>) {
            state.ongoingPage = action.payload;
        },
        setHistoryPage(state, action: PayloadAction<number>) {
            state.historyPage = action.payload;
        },
        
        setPendings(state, action: PayloadAction<Booking[]>) {
            state.userPendingBookings = action.payload;
        },
        setUpcomings(state, action: PayloadAction<Booking[]>) {
            state.userUpcomingBookings = action.payload;
        },
        setOngoings(state, action: PayloadAction<Booking[]>) {
            state.userOngoingBookings = action.payload;
        },
        setHistorys(state, action: PayloadAction<Booking[]>) {
            state.userHistoryBookings = action.payload;
        },
        setTotalPending(state, action: PayloadAction<number>) {
            state.totalPending = action.payload;
        },
        setTotalUpcoming(state, action: PayloadAction<number>) {
            state.totalUpcoming = action.payload;
        },
        setTotalOngoing(state, action: PayloadAction<number>) {
            state.totalOngoing = action.payload;
        },
        setTotalHistory(state, action: PayloadAction<number>) {
            state.totalHistory = action.payload;
        },
        setFilters(state, action: PayloadAction<Record<string, string>>) {
            state.filters = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getPendingData.pending, (state) => {
                state.pendingBtnLoading = true;
                state.isPendingLoading = true;
                state.error = null;
            })
            .addCase(
                getPendingData.fulfilled,
                (
                    state,
                    action: PayloadAction<InlineResponse2001 | undefined>
                ) => {
                    const resPendingBookings = action.payload as unknown as InlineResponse2001;
                    const items = JSON.parse(
                                        JSON.stringify(resPendingBookings.results)
                                    );
                    const count = resPendingBookings.count;
                    state.totalPending = count;
                    state.pendingBtnLoading = false;
                    state.isPendingLoading = false;

                    if (state.pendingPage === 1) {
                        state.userPendingBookings = items;
                    } else {
                        if (items.length > 0) {
                            state.userPendingBookings.push(...items);
                        }
                    }
                }
            )
            .addCase(getPendingData.rejected, (state, action) => {
                state.pendingBtnLoading = false;

                state.error = action.payload as string;
            })
            // Upcoming Bookings
            .addCase(getUpcomingData.pending, (state) => {
                state.upcomingBtnLoading = true;
                state.isUpcomingLoading = true;
                state.error = null;
            })
            .addCase(
                getUpcomingData.fulfilled,
                (
                    state,
                    action: PayloadAction<InlineResponse2001 | undefined>
                ) => {
                    const resUpcomingBookings = action.payload as unknown as InlineResponse2001;
                    const items = JSON.parse(
                                        JSON.stringify(resUpcomingBookings.results)
                                    );
                    const count = resUpcomingBookings.count;
                    state.totalUpcoming = count;
                    state.upcomingBtnLoading = false;
                    state.isUpcomingLoading = false;

                    if (state.upcomingPage === 1) {
                        state.userUpcomingBookings = items;
                    } else {
                        if (items.length > 0) {
                            state.userUpcomingBookings.push(...items);
                        }
                    }
                }
            )
            .addCase(getUpcomingData.rejected, (state, action) => {
                state.upcomingBtnLoading = false;

                state.error = action.payload as string;
            })
            // Ongoing Bookings
            .addCase(getOngoingData.pending, (state) => {
                state.ongoingBtnLoading = true;
                state.isOngoingLoading = true;
                state.error = null;
            })
            .addCase(
                getOngoingData.fulfilled,
                (
                    state,
                    action: PayloadAction<InlineResponse2001 | undefined>
                ) => {
                    const resOngoingBookings = action.payload as unknown as InlineResponse2001;
                    const items = JSON.parse(
                                        JSON.stringify(resOngoingBookings.results)
                                    );
                    const count = resOngoingBookings.count;
                    state.totalOngoing = count;
                    state.ongoingBtnLoading = false;
                    state.isOngoingLoading = false;

                    if (state.ongoingPage === 1) {
                        state.userOngoingBookings = items;
                    } else {
                        if (items.length > 0) {
                            state.userOngoingBookings.push(...items);
                        }
                    }
                }
            )
            .addCase(getOngoingData.rejected, (state, action) => {
                state.ongoingBtnLoading = false;

                state.error = action.payload as string;
            })
            // History Bookings
            .addCase(getHistoryData.pending, (state) => {
                state.historyBtnLoading = true;
                state.isHistoryLoading = true;
                state.error = null;
            })
            .addCase(
                getHistoryData.fulfilled,
                (
                    state,
                    action: PayloadAction<InlineResponse2001 | undefined>
                ) => {
                    const resHistoryBookings = action.payload as unknown as InlineResponse2001;
                    const items = JSON.parse(
                                        JSON.stringify(resHistoryBookings.results)
                                    );
                    state.totalHistory = resHistoryBookings.count;
                    state.historyBtnLoading = false;
                    state.isHistoryLoading = false;

                    if (state.historyPage === 1) {
                        state.userHistoryBookings = items;
                    } else {
                        if (items.length > 0) {
                            state.userHistoryBookings.push(...items);
                        }
                    }
                }
            )
            .addCase(getHistoryData.rejected, (state, action) => {
                state.historyBtnLoading = false;

                state.error = action.payload as string;
            })
    }
});

export const {
    setActiveButton,
    setPendingPage,
    setUpcomingPage,
    setOngoingPage,
    setHistoryPage,
    setPendings,
    setUpcomings,
    setOngoings,
    setHistorys,
    setTotalPending,
    setTotalUpcoming,
    setTotalOngoing,
    setTotalHistory,
    setFilters
} = bookingSlice.actions;

const persistConfig = {
    key: 'booking',
    storage,
    blacklist: ['pendingPage', 'upcomingPage', 'ongoingPage', 'historyPage']
};

export default persistReducer(persistConfig, bookingSlice.reducer);
